import type { FC } from "react";
import { Skeleton } from "primereact/skeleton";
import { deriveKpiTotals } from "@/pages/CampaignOverviewPage/utils/kpiDerivation";
import type { ArrivalReportRow, PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import { COMPACT_NUMBER_FORMATTER } from "@/utils/tableFormatters";
import styles from "@/pages/CampaignOverviewPage/widgets/KpiTiles.module.css";

interface KpiTilesProps {
  arrivalError: string | null;
  arrivalLoading: boolean;
  arrivalRows: ArrivalReportRow[];
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

// Performance tiles only — Projected Visits has its own inline loading state
// so the 7 performance tiles never block on the arrival-standard fetch.
const PERF_TILE_COUNT = 7;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  currency: "USD",
  maximumFractionDigits: 0,
  style: "currency",
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: "percent",
});

const KpiTiles: FC<KpiTilesProps> = ({
  arrivalError,
  arrivalLoading,
  arrivalRows,
  error,
  isLoading,
  rows,
}) => {
  if (error) {
    return <p className={styles.message}>{TEXT.campaignOverview.kpi.error}</p>;
  }

  if (isLoading) {
    return (
      <div className={styles.grid}>
        {Array.from({ length: PERF_TILE_COUNT + 1 }, (_, index) => (
          <div key={index} className={styles.tile}>
            <Skeleton
              height="0.75rem"
              pt={{ root: { className: styles.skeleton } }}
              width="55%"
            />
            <Skeleton
              height="2.25rem"
              pt={{ root: { className: styles.skeleton } }}
              width="80%"
            />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0 && !arrivalLoading && arrivalRows.length === 0) {
    return <p className={styles.message}>{TEXT.campaignOverview.kpi.noData}</p>;
  }

  const totals = deriveKpiTotals(rows, arrivalRows);

  const perfTiles = [
    {
      key: "impressions",
      label: TEXT.campaignOverview.kpi.impressions,
      value: COMPACT_NUMBER_FORMATTER.format(totals.impressions),
    },
    {
      key: "clicks",
      label: TEXT.campaignOverview.kpi.clicks,
      value: COMPACT_NUMBER_FORMATTER.format(totals.clicks),
    },
    {
      key: "totalSpend",
      label: TEXT.campaignOverview.kpi.totalSpend,
      value: currencyFormatter.format(totals.totalSpend),
    },
    {
      key: "ctr",
      label: TEXT.campaignOverview.kpi.ctr,
      value: percentFormatter.format(totals.ctr),
    },
    {
      key: "vcr",
      label: TEXT.campaignOverview.kpi.vcr,
      value: totals.vcr !== null ? percentFormatter.format(totals.vcr) : null,
    },
    {
      key: "cpa",
      label: TEXT.campaignOverview.kpi.cpa,
      value: currencyFormatter.format(totals.cpa),
    },
    {
      key: "conversions",
      label: TEXT.campaignOverview.kpi.conversions,
      value: COMPACT_NUMBER_FORMATTER.format(totals.conversions),
    },
  ];

  // Projected Visits resolves independently — show its own skeleton while
  // arrival-standard is still in flight so the other 7 tiles are not held up.
  const projectedVisitsValue =
    arrivalError !== null || totals.projectedVisits === null
      ? "—"
      : COMPACT_NUMBER_FORMATTER.format(totals.projectedVisits);

  const projectedVisitsMuted =
    totals.projectedVisits === null || arrivalError !== null;

  return (
    <div className={styles.grid}>
      {perfTiles.map((tile) => (
        <div key={tile.key} className={styles.tile}>
          <span className={styles.label}>{tile.label}</span>
          <span
            className={tile.value === null ? styles.valueMuted : styles.value}
          >
            {tile.value ?? TEXT.campaignOverview.kpi.notApplicable}
          </span>
        </div>
      ))}
      <div className={styles.tile}>
        {arrivalLoading ? (
          <>
            <Skeleton
              height="0.75rem"
              pt={{ root: { className: styles.skeleton } }}
              width="55%"
            />
            <Skeleton
              height="2.25rem"
              pt={{ root: { className: styles.skeleton } }}
              width="80%"
            />
          </>
        ) : (
          <>
            <span className={styles.label}>
              {TEXT.campaignOverview.kpi.projectedVisits}
            </span>
            <span
              className={
                projectedVisitsMuted ? styles.valueMuted : styles.value
              }
            >
              {projectedVisitsValue}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default KpiTiles;
